import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import {
  getAllRechargeRequests,
  getAllProfiles,
  approveRechargeRequest,
  rejectRechargeRequest,
  adjustBalance,
  toMoney,
} from "@/services/supabaseService";
import type { RechargeRequest, Profile } from "@/lib/supabase";
import AdminLayout from "@/components/feature/AdminLayout";

export default function RechargeAudit() {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [requests, setRequests] = useState<RechargeRequest[]>([]);
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<"all" | "待審核" | "通過" | "拒絕">("all");
  const [previewImage, setPreviewImage] = useState("");
  const [previewImageError, setPreviewImageError] = useState(false);
  const [rejectModal, setRejectModal] = useState<RechargeRequest | null>(null);
  const [rejectNote, setRejectNote] = useState("");

  // Manual balance adjustment
  const [showManualModal, setShowManualModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState("");
  const [manualAmount, setManualAmount] = useState("");
  const [manualNote, setManualNote] = useState("");

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [r, p] = await Promise.all([getAllRechargeRequests(), getAllProfiles()]);
      setRequests(r);
      setProfiles(p);
    } catch {
      // fallback
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  if (!user || !isAdmin) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center py-20">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <i className="ri-forbid-line text-4xl text-gray-300"></i>
          </div>
          <p className="text-gray-500 text-lg mb-4">權限不足</p>
          <p className="text-sm text-gray-400 mb-6">只有管理員才能訪問此頁面</p>
          <button onClick={() => navigate("/")} className="px-6 py-2.5 bg-amber-500 text-white rounded-full text-sm font-medium cursor-pointer">返回首頁</button>
        </div>
      </div>
    );
  }

  const filtered = filterStatus === "all" ? requests : requests.filter((r) => r.status === filterStatus);

  const handleApprove = async (req: RechargeRequest) => {
    try {
      await approveRechargeRequest(req.id);
      await loadData();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : "核准失敗");
    }
  };

  const handleReject = async (req: RechargeRequest) => {
    if (!rejectNote.trim()) return;
    try {
      await rejectRechargeRequest(req.id, rejectNote);
      await loadData();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : "拒絕失敗");
    }
    setRejectModal(null);
    setRejectNote("");
  };

  const stats = {
    total: requests.length,
    pending: requests.filter((r) => r.status === "待審核").length,
    approved: requests.filter((r) => r.status === "通過").length,
    rejected: requests.filter((r) => r.status === "拒絕").length,
  };

  const handleManualTopUp = async () => {
    const numAmount = Number(manualAmount);
    if (!selectedUser || !numAmount || numAmount <= 0) return;
    const note = manualNote.trim() || "管理員後台手動入帳";
    try {
      await adjustBalance(selectedUser, numAmount, note);
      await loadData();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : "入帳失敗");
    }
    setShowManualModal(false);
    setSelectedUser("");
    setManualAmount("");
    setManualNote("");
  };

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold text-gray-900">儲值審核中心</h1>
            <p className="text-sm text-gray-500 mt-0.5">審核用戶儲值申請，或手動為用戶入帳</p>
          </div>
          <button
            onClick={() => setShowManualModal(true)}
            className="px-4 py-2.5 rounded-xl bg-amber-500 text-white text-sm font-medium hover:bg-amber-600 transition-all cursor-pointer flex items-center gap-2 whitespace-nowrap shadow-sm"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-add-circle-line text-xs"></i>
            </div>
            手動入帳
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: "全部", value: stats.total, color: "bg-gray-100 text-gray-700" },
            { label: "待審核", value: stats.pending, color: "bg-amber-100 text-amber-700" },
            { label: "已通過", value: stats.approved, color: "bg-emerald-100 text-emerald-700" },
            { label: "已拒絕", value: stats.rejected, color: "bg-red-100 text-red-700" },
          ].map((s) => (
            <button
              key={s.label}
              onClick={() => setFilterStatus(s.label === "全部" ? "all" : (s.label === "待審核" ? "待審核" : s.label === "已通過" ? "通過" : "拒絕"))}
              className={`rounded-xl p-4 text-center transition-all cursor-pointer shadow-sm ${s.color} ${
                (s.label === "全部" && filterStatus === "all") ||
                (s.label === "待審核" && filterStatus === "待審核") ||
                (s.label === "已通過" && filterStatus === "通過") ||
                (s.label === "已拒絕" && filterStatus === "拒絕")
                  ? "ring-2 ring-amber-400"
                  : ""
              }`}
            >
              <p className="text-2xl font-bold">{s.value}</p>
              <p className="text-xs mt-1">{s.label}</p>
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          {isLoading ? (
            <div className="text-center py-12 text-gray-400 text-sm">載入中...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">申請人</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">金額</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">後五碼</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">憑證</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">時間</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">狀態</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filtered.map((req) => (
                    <tr key={req.id} className="hover:bg-gray-50/50">
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-800">{req.user_name}</p>
                        <p className="text-xs text-gray-400">{req.user_email}</p>
                      </td>
                      <td className="px-4 py-3 font-bold text-amber-600">${toMoney(req.amount).toLocaleString()}</td>
                      <td className="px-4 py-3 text-gray-700 font-mono">{req.transfer_code}</td>
                      <td className="px-4 py-3">
                        {req.voucher_url && (
                          <button
                            onClick={() => { setPreviewImage(req.voucher_url || ""); setPreviewImageError(false); }}
                            className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-amber-100 hover:text-amber-600 transition-colors cursor-pointer"
                          >
                            <i className="ri-image-line text-sm"></i>
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-400">{new Date(req.created_at).toLocaleString("zh-TW")}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          req.status === "待審核"
                            ? "bg-amber-100 text-amber-700"
                            : req.status === "通過"
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-red-100 text-red-700"
                        }`}>
                          {req.status}
                        </span>
                        {req.admin_note && (
                          <p className="text-xs text-red-400 mt-1">{req.admin_note}</p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {req.status === "待審核" && (
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleApprove(req)}
                              className="px-3 py-1.5 rounded-lg bg-emerald-500 text-white text-xs font-medium hover:bg-emerald-600 cursor-pointer"
                            >
                              核准
                            </button>
                            <button
                              onClick={() => { setRejectModal(req); setRejectNote(""); }}
                              className="px-3 py-1.5 rounded-lg bg-red-500 text-white text-xs font-medium hover:bg-red-600 cursor-pointer"
                            >
                              拒絕
                            </button>
                          </div>
                        )}
                        {req.status === "通過" && (
                          <span className="text-xs text-emerald-600">
                            <i className="ri-check-line mr-1"></i>已入帳
                          </span>
                        )}
                        {req.status === "拒絕" && (
                          <span className="text-xs text-red-400">
                            <i className="ri-close-line mr-1"></i>已拒絕
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!isLoading && filtered.length === 0 && (
            <div className="text-center py-12 text-gray-400 text-sm">暫無符合條件的申請</div>
          )}
        </div>
      </div>

      {/* Image preview modal */}
      {previewImage && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setPreviewImage("")}>
          <div className="bg-white rounded-xl overflow-hidden max-w-2xl max-h-[80vh]" onClick={(e) => e.stopPropagation()}>
            {!previewImageError ? (
              <img
                src={previewImage}
                alt="憑證"
                className="max-w-full max-h-[70vh] object-contain"
                onError={() => setPreviewImageError(true)}
              />
            ) : (
              <div className="flex flex-col items-center justify-center py-12 px-8 text-gray-400">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-gray-100 mb-3">
                  <i className="ri-image-line text-2xl"></i>
                </div>
                <p className="text-sm">憑證圖片載入失敗</p>
                <a
                  href={previewImage}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-amber-600 hover:text-amber-700 mt-1 cursor-pointer"
                >
                  嘗試在新分頁開啟
                </a>
              </div>
            )}
            <div className="p-3 text-center">
              <button
                onClick={() => setPreviewImage("")}
                className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200 cursor-pointer"
              >
                關閉
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject modal */}
      {rejectModal && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-bold text-gray-900 mb-2">拒絕儲值申請</h3>
            <p className="text-sm text-gray-500 mb-4">
              {rejectModal.user_name} · ${toMoney(rejectModal.amount).toLocaleString()}
            </p>
            <label className="text-sm text-gray-600 mb-1.5 block">拒絕原因（必填）</label>
            <textarea
              value={rejectNote}
              onChange={(e) => setRejectNote(e.target.value)}
              placeholder="例如：匯款金額與申請不符、憑證不清晰..."
              rows={3}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:border-amber-400 resize-none mb-4"
            />
            <div className="flex gap-3">
              <button
                onClick={() => { setRejectModal(null); setRejectNote(""); }}
                className="flex-1 py-2.5 rounded-lg border border-gray-200 text-gray-700 text-sm font-medium hover:bg-gray-50 cursor-pointer"
              >
                取消
              </button>
              <button
                onClick={() => handleReject(rejectModal)}
                className="flex-1 py-2.5 rounded-lg bg-red-500 text-white text-sm font-medium hover:bg-red-600 cursor-pointer"
              >
                確認拒絕
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manual top-up modal */}
      {showManualModal && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-bold text-gray-900 mb-2">手動入帳</h3>
            <p className="text-sm text-gray-500 mb-4">直接在後台為用戶增加錢包餘額</p>
            <div className="space-y-4">
              <div>
                <label className="text-sm text-gray-600 mb-1.5 block">選擇用戶</label>
                <select
                  value={selectedUser}
                  onChange={(e) => setSelectedUser(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm outline-none focus:border-amber-400"
                >
                  <option value="">請選擇分銷商</option>
                  {profiles.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name} ({u.email}) — 當前餘額 ${toMoney(u.balance).toLocaleString()}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm text-gray-600 mb-1.5 block">入帳金額（$）</label>
                <input
                  type="number"
                  value={manualAmount}
                  onChange={(e) => setManualAmount(e.target.value)}
                  placeholder="輸入金額"
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm outline-none focus:border-amber-400"
                />
              </div>
              <div>
                <label className="text-sm text-gray-600 mb-1.5 block">備註</label>
                <input
                  type="text"
                  value={manualNote}
                  onChange={(e) => setManualNote(e.target.value)}
                  placeholder="例如：LINE 確認收款、銀行轉帳..."
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm outline-none focus:border-amber-400"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => { setShowManualModal(false); setSelectedUser(""); setManualAmount(""); setManualNote(""); }}
                className="flex-1 py-2.5 rounded-lg border border-gray-200 text-gray-700 text-sm font-medium hover:bg-gray-50 cursor-pointer"
              >
                取消
              </button>
              <button
                onClick={handleManualTopUp}
                disabled={!selectedUser || !manualAmount || Number(manualAmount) <= 0}
                className="flex-1 py-2.5 rounded-lg bg-amber-500 text-white text-sm font-medium hover:bg-amber-600 disabled:opacity-50 cursor-pointer"
              >
                確認入帳
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}